import React, { useState, useEffect, useCallback } from "react";
import { Rocket, Plus, Trash2, Loader2, Copy, ExternalLink, ArrowLeft, Lock, Sparkles, Star, Wrench, Receipt, ClipboardCheck, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  fetchProposals, createProposal, updateProposal, deleteProposal, proposalLink, sendProposal,
  fetchEquipment, fetchAddons, fetchCustomers, monthlyPayment, money, REQUIRED_MAINTENANCE_PLAN, proposalInsights, fetchDiscounts,
} from "@/lib/dashboardApi";

const TIERS = ["good", "better", "best"];
const LABEL = { good: "GOOD · Essential Comfort", better: "BETTER · Enhanced Comfort", best: "BEST · Ultimate Comfort" };
const inputCls = "w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-slate-800 text-sm focus:outline-none focus:border-[#F97316]";
const emptyTier = () => ({ equipment_id: "", addon_ids: [], install_kit_ids: [], other_charges: [], recommended: false, price_override: "", lifetime: false, maintenance_plan: "", notes: "" });
const DISCOVERY = [
  ["reason", "Reason for replacement"], ["existing_system", "Existing system"], ["equipment_age", "Equipment age"],
  ["system_condition", "Current condition"], ["comfort_concerns", "Comfort concerns"], ["efficiency_concerns", "Efficiency concerns"],
  ["iaq_concerns", "Indoor-air-quality concerns"], ["humidity_concerns", "Humidity concerns"], ["priorities", "Customer priorities"],
  ["budget", "Budget considerations"], ["desired_improvements", "Desired improvements"], ["sales_notes", "Sales notes"],
];

const LaunchSelect = () => {
  const [view, setView] = useState("list");
  const [proposals, setProposals] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [addons, setAddons] = useState([]);
  const [installKit, setInstallKit] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [allDiscounts, setAllDiscounts] = useState([]);
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const prefillDone = React.useRef(false);
  const [showMonthly, setShowMonthly] = useState(false);
  const [insights, setInsights] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const runAI = async () => {
    setAiLoading(true); setInsights("");
    try { const r = await proposalInsights(p.id); setInsights(r.insights || "No insights returned."); }
    catch { toast.error("AI insights unavailable right now."); }
    finally { setAiLoading(false); }
  };

  const load = useCallback(async () => {
    try {
      setProposals(await fetchProposals());
      setEquipment(await fetchEquipment());
      setAddons(await fetchAddons());
      try { setInstallKit((await api.get("/install-kit")).data); } catch { /* optional */ }
      setCustomers(await fetchCustomers());
      try { const d = await fetchDiscounts(); setAllDiscounts((d.items || []).filter((x) => x.amount != null && x.active)); } catch { /* optional */ }
    } catch (e) { console.error(e); }
    finally { setLoaded(true); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!loaded || prefillDone.current) return;
    const raw = sessionStorage.getItem("launchSelectPrefill");
    if (!raw) return;
    prefillDone.current = true;
    sessionStorage.removeItem("launchSelectPrefill");
    let pf; try { pf = JSON.parse(raw); } catch { return; }
    (async () => {
      if (pf.mode === "gbb_systems") {
        // Full Good/Better/Best prefill from the Selling Comfort page: three matched systems.
        try {
          const te = pf.tiers_eq || {};
          const tiers = {
            good: { ...emptyTier(), equipment_id: te.good || "" },
            better: { ...emptyTier(), equipment_id: te.better || "" },
            best: { ...emptyTier(), equipment_id: te.best || "" },
          };
          const doc = await createProposal({
            tiers, popular_tier: pf.popular_tier || "better", recommended_tier: pf.popular_tier || "better",
            discounts: pf.discounts || [],
            customer_id: pf.customer_id || "", customer_name: pf.customer_name || "",
            customer_phone: pf.customer_phone || "", customer_email: pf.customer_email || "",
            customer_address: pf.customer_address || "",
            title: (pf.title || `Comfort System Options — ${pf.customer_name || ""}`).slice(0, 120),
          });
          setP(doc); setView("edit"); load();
          toast.success(`Good/Better/Best prefilled from ${pf.source || "Selling Comfort"}. Review & finalize.`);
        } catch { toast.error("Could not start proposal from Selling Comfort."); }
        return;
      }
      if (pf.mode === "sales_opportunity") {
        // Structure-only prefill: empty Good/Better/Best + customer info. Consultant selects everything.
        try {
          const doc = await createProposal({
            tiers: { good: emptyTier(), better: emptyTier(), best: emptyTier() }, popular_tier: "better",
            customer_id: pf.customer_id || "", customer_name: pf.customer_name || "",
            customer_phone: pf.customer_phone || "", customer_email: pf.customer_email || "",
            customer_address: pf.customer_address || "",
            title: `Comfort System Options — ${pf.customer_name || ""}`.slice(0, 120),
          });
          setP(doc); setView("edit"); load();
          toast.success(`New sales opportunity for ${pf.customer_name || "customer"}. Build Good/Better/Best — nothing is pre-selected.`);
        } catch { toast.error("Could not start sales opportunity."); }
        return;
      }
      const key = ["good", "better", "best"].includes(pf.tier) ? pf.tier : "better";
      const brand = (pf.brand || "").toLowerCase();
      const cand = equipment.filter((e) => `${e.brand} ${e.model}`.toLowerCase().includes(brand));
      const ton = pf.tonnage ? String(parseFloat(pf.tonnage)) : "";
      const match = (ton && cand.find((e) => String(e.tonnage || "").includes(ton))) || cand[0];
      const tiers = { good: emptyTier(), better: emptyTier(), best: emptyTier() };
      tiers[key] = { ...emptyTier(), equipment_id: match ? match.id : "", notes: pf.notes || "" };
      try {
        const doc = await createProposal({ tiers, popular_tier: key, title: `${pf.source || "Matchup"} — ${pf.label || ""}`.slice(0, 120) });
        setP(doc); setView("edit"); load();
        toast.success(`Prefilled ${key.toUpperCase()} from ${pf.source || "matchup"}${match ? " · system matched" : ""}. Review & finalize.`);
      } catch { toast.error("Could not start proposal from matchup."); }
    })();
  }, [loaded, equipment, load]);

  const openNew = async () => {
    const doc = await createProposal({ tiers: { good: emptyTier(), better: emptyTier(), best: emptyTier() }, popular_tier: "better" });
    setP(doc); setView("edit"); load();
  };
  const openEdit = (doc) => { setP({ ...doc, tiers: { good: emptyTier(), better: emptyTier(), best: emptyTier(), ...(doc.tiers || {}) } }); setView("edit"); };
  const remove = async (doc) => { if (!window.confirm("Delete this proposal?")) return; await deleteProposal(doc.id); load(); };

  const setField = (k, v) => setP((x) => ({ ...x, [k]: v }));
  // Equipment catalog filters (Load Calc NOT required — techs pick these directly).
  const [eqFilter, setEqFilter] = useState({ tonnage: "", config: "", mode: "" });
  const matched = equipment.filter((e) => e.source === "catalog_matchup");
  const tonnageOpts = Array.from(new Set(matched.map((e) => e.tonnage).filter(Boolean)))
    .sort((a, b) => parseFloat(a) - parseFloat(b));
  const passFilter = (e) =>
    (!eqFilter.tonnage || e.tonnage === eqFilter.tonnage) &&
    (!eqFilter.config || e.config === eqFilter.config) &&
    (!eqFilter.mode || e.mode === eqFilter.mode);
  const setTier = (key, patch) => setP((x) => ({ ...x, tiers: { ...x.tiers, [key]: { ...x.tiers[key], ...patch } } }));
  const setTierFin = (key, patch) => setP((x) => ({ ...x, tiers: { ...x.tiers, [key]: { ...x.tiers[key], financing: { ...(x.tiers[key].financing || {}), ...patch } } } }));
  const toggleLifetime = (key, on) => setTier(key, { lifetime: on, maintenance_plan: on ? REQUIRED_MAINTENANCE_PLAN : "" });
  const toggleAddon = (key, id) => setP((x) => {
    const cur = x.tiers[key].addon_ids || [];
    const next = cur.includes(id) ? cur.filter((a) => a !== id) : [...cur, id];
    return { ...x, tiers: { ...x.tiers, [key]: { ...x.tiers[key], addon_ids: next } } };
  });
  const loadCustomer = (id) => {
    const c = customers.find((x) => x.id === id);
    if (c) setP((x) => ({ ...x, customer_id: c.id, customer_name: c.name || "", customer_phone: c.phone || "", customer_email: c.email || "", customer_address: c.address || "" }));
  };

  const tierTotal = (t) => {
    if (t.price_override !== "" && t.price_override != null) return Number(t.price_override) || 0;
    const eq = equipment.find((e) => e.id === t.equipment_id);
    const addSum = (t.addon_ids || []).reduce((s, id) => s + (Number(addons.find((a) => a.id === id)?.price) || 0), 0);
    const kitSum = (t.install_kit_ids || []).reduce((s, id) => s + (Number(installKit.find((k) => k.id === id)?.price) || 0), 0);
    const ocSum = (t.other_charges || []).reduce((s, o) => s + (Number(o.amount) || 0), 0);
    return (Number(eq?.price) || 0) + addSum + kitSum + ocSum;
  };
  const toggleKit = (key, item) => setP((x) => {
    const cur = x.tiers[key].install_kit_ids || [];
    let next;
    if (item.group === "line_set") {
      const others = cur.filter((id) => !installKit.find((k) => k.id === id && k.group === "line_set"));
      next = cur.includes(item.id) ? others : [...others, item.id];
    } else {
      next = cur.includes(item.id) ? cur.filter((id) => id !== item.id) : [...cur, item.id];
    }
    return { ...x, tiers: { ...x.tiers, [key]: { ...x.tiers[key], install_kit_ids: next } } };
  });
  const selectAllKit = (key) => setP((x) => {
    const lineSets = installKit.filter((k) => k.group === "line_set");
    const nonLine = installKit.filter((k) => k.group !== "line_set").map((k) => k.id);
    const chosenLine = (x.tiers[key].install_kit_ids || []).find((id) => lineSets.some((k) => k.id === id)) || (lineSets[0] && lineSets[0].id);
    return { ...x, tiers: { ...x.tiers, [key]: { ...x.tiers[key], install_kit_ids: [...nonLine, ...(chosenLine ? [chosenLine] : [])] } } };
  });
  const setRecommended = (key) => setP((x) => ({ ...x, recommended_tier: key,
    tiers: Object.fromEntries(Object.entries(x.tiers).map(([k, v]) => [k, { ...v, recommended: k === key }])) }));
  const fin = p?.financing || { apr: 9.99, term_months: 120, deposit_pct: 10 };
  const appliedDiscounts = p?.discounts || [];
  const applyDisc = (base) => {
    let total = 0; const lines = [];
    for (const d of appliedDiscounts) {
      const amt = d.amount; if (amt == null || amt === "") continue;
      const raw = d.kind === "percent" ? base * (Number(amt) / 100) : Number(amt);
      const a = Math.max(0, Math.min(raw, base - total)); if (a <= 0) continue;
      total += a; lines.push({ name: d.name, amount: a });
    }
    return { final: Math.max(0, base - total), total, lines, pct: base ? (total / base) * 100 : 0 };
  };
  const toggleProposalDiscount = (d) => setP((x) => {
    const cur = x.discounts || []; const on = cur.some((c) => c.id === d.id);
    return { ...x, discounts: on ? cur.filter((c) => c.id !== d.id) : [...cur, d] };
  });
  const setOpp = (k, v) => setP((x) => ({ ...x, opportunity: { ...(x.opportunity || {}), [k]: v } }));
  const genInvoice = async () => {
    try { const { data } = await api.post(`/proposals/${p.id}/invoice`); toast.success(`Invoice ${data.number} created — ${money(data.amount)}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Proposal must be signed first."); }
  };
  const viewHandoff = async () => {
    try { const { data } = await api.get(`/proposals/${p.id}/handoff`);
      const kit = (data.install_kit || []).map((k) => k.name).join(", ") || "none";
      toast.success(`Handoff ready: ${data.equipment?.brand || ""} ${data.equipment?.model || ""} · line-set: ${data.line_set || "—"} · crane: ${data.crane ? "yes" : "no"} · kit: ${kit}`, { duration: 8000 }); }
    catch (e) { toast.error(e.response?.data?.detail || "Proposal must be signed first."); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateProposal(p.id, {
        customer_id: p.customer_id, customer_name: p.customer_name, customer_phone: p.customer_phone,
        customer_email: p.customer_email, customer_address: p.customer_address, title: p.title,
        tiers: p.tiers, popular_tier: p.popular_tier, recommended_tier: p.recommended_tier || "",
        opportunity: p.opportunity || {}, salesperson: p.salesperson || "",
        financing: p.financing, discounts: p.discounts || [], status: "sent",
      });
      toast.success("Proposal saved");
      load();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const copyLink = () => { navigator.clipboard.writeText(proposalLink(p)); toast.success("Customer link copied"); };
  const sendToCustomer = async () => {
    if (!p.customer_email) { toast.error("Add a customer email first, then Save."); return; }
    setSaving(true);
    try {
      await sendProposal(p.id, window.location.origin);
      toast.success(`Proposal emailed to ${p.customer_email} — automated follow-up started`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send proposal"); }
    finally { setSaving(false); }
  };

  if (view === "list") {
    return (
      <div className="space-y-4" data-testid="launch-select-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><Rocket className="w-5 h-5 text-[#F97316]" /><h3 className="text-slate-900 font-semibold text-lg">LAUNCH SELECT™ Proposals</h3></div>
          <button data-testid="ls-new" onClick={openNew} className="flex items-center gap-2 bg-[#F97316] hover:bg-[#ea6a0c] text-white px-4 py-2 rounded-lg font-semibold text-sm"><Plus className="w-4 h-4" /> New Proposal</button>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
          {proposals.length === 0 ? <p className="text-slate-400 text-sm py-10 text-center">No proposals yet — create one.</p> :
            proposals.map((d) => (
              <div key={d.id} className="flex items-center justify-between p-4" data-testid={`ls-row-${d.id}`}>
                <div><p className="font-semibold text-slate-900">{d.customer_name || "Unnamed customer"}</p><p className="text-xs text-slate-500">{d.title} · <span className={`font-semibold ${d.status === "sold" ? "text-green-600" : "text-slate-500"}`}>{d.status}{d.selected_tier ? ` · ${d.selected_tier.toUpperCase()}` : ""}</span></p></div>
                <div className="flex items-center gap-2">
                  <a href={proposalLink(d)} target="_blank" rel="noreferrer" className="text-slate-500 hover:text-[#F97316] p-2" title="Open customer view"><ExternalLink className="w-4 h-4" /></a>
                  <button onClick={() => openEdit(d)} className="text-sm font-semibold text-[#12355B] hover:underline">Edit</button>
                  <button onClick={() => remove(d)} className="text-slate-300 hover:text-red-600 p-2"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="ls-editor">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <button onClick={() => { setView("list"); setP(null); }} className="flex items-center gap-1 text-slate-600 hover:text-slate-900 text-sm"><ArrowLeft className="w-4 h-4" /> Back</button>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowMonthly((v) => !v)} data-testid="ls-toggle-monthly" className="text-sm font-semibold border border-slate-300 rounded-lg px-3 py-2 text-slate-700">{showMonthly ? "Show Total" : "Show Monthly"}</button>
          <button onClick={copyLink} data-testid="ls-copy-link" className="flex items-center gap-1.5 text-sm font-semibold border border-slate-300 rounded-lg px-3 py-2 text-slate-700"><Copy className="w-4 h-4" /> Copy Link</button>
          <button onClick={sendToCustomer} disabled={saving || p.status === "sold"} data-testid="ls-send-customer" className="flex items-center gap-1.5 text-sm font-semibold border border-[#F97316] text-[#F97316] hover:bg-orange-50 rounded-lg px-3 py-2 disabled:opacity-60"><Send className="w-4 h-4" /> Send to Customer</button>
          <a href={proposalLink(p)} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-sm font-semibold border border-slate-300 rounded-lg px-3 py-2 text-slate-700"><ExternalLink className="w-4 h-4" /> Preview</a>
          <button onClick={runAI} disabled={aiLoading} data-testid="ls-ai" className="flex items-center gap-1.5 text-sm font-semibold border border-slate-300 rounded-lg px-3 py-2 text-slate-700">{aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 text-[#F97316]" />} AI Insights</button>
          <button onClick={save} disabled={saving || p.status === "sold"} data-testid="ls-save" className="flex items-center gap-2 bg-[#F97316] hover:bg-[#ea6a0c] text-white px-4 py-2 rounded-lg font-semibold text-sm disabled:opacity-60">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save</button>
        </div>
      </div>

      {p.status === "sold" && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3" data-testid="ls-sold-banner">
          <p className="text-sm font-semibold text-green-800 flex items-center gap-2"><Lock className="w-4 h-4" /> SOLD & LOCKED — signed {(p.signed_at || "").slice(0, 10)}. Price is frozen; edits are disabled.</p>
          <div className="flex items-center gap-2">
            <button onClick={viewHandoff} data-testid="ls-handoff" className="flex items-center gap-1.5 text-sm font-semibold border border-green-300 bg-white rounded-lg px-3 py-2 text-green-800"><ClipboardCheck className="w-4 h-4" /> Installation Handoff</button>
            <button onClick={genInvoice} data-testid="ls-invoice" className="flex items-center gap-1.5 text-sm font-semibold bg-[#12355B] text-white rounded-lg px-3 py-2"><Receipt className="w-4 h-4" /> Generate Invoice</button>
          </div>
        </div>
      )}

      {/* Sales Opportunity discovery */}
      <details className="bg-white border border-slate-200 rounded-xl p-4" data-testid="ls-discovery">
        <summary className="cursor-pointer font-semibold text-[#12355B] text-sm flex items-center gap-2"><Wrench className="w-4 h-4 text-[#F97316]" /> Sales Opportunity — Discovery Notes</summary>
        <div className="grid md:grid-cols-2 gap-3 mt-3">
          {DISCOVERY.map(([k, lbl]) => (
            <div key={k} className={k === "sales_notes" || k === "desired_improvements" ? "md:col-span-2" : ""}>
              <label className="text-[11px] font-semibold text-slate-500">{lbl}</label>
              <input value={(p.opportunity || {})[k] || ""} onChange={(e) => setOpp(k, e.target.value)} className={inputCls} data-testid={`ls-opp-${k}`} />
            </div>
          ))}
          <div><label className="text-[11px] font-semibold text-slate-500">Comfort Consultant</label><input value={p.salesperson || ""} onChange={(e) => setField("salesperson", e.target.value)} className={inputCls} placeholder="Salesperson name" /></div>
        </div>
      </details>

      {/* Customer + financing */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-xs font-semibold text-slate-500">Load existing customer</label>
          <select onChange={(e) => loadCustomer(e.target.value)} className={inputCls} data-testid="ls-customer-select" defaultValue=""><option value="">— New / select —</option>{customers.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.phone}</option>)}</select>
          <input value={p.customer_name || ""} onChange={(e) => setField("customer_name", e.target.value)} className={inputCls} placeholder="Customer name" data-testid="ls-customer-name" />
          <div className="grid grid-cols-2 gap-2"><input value={p.customer_phone || ""} onChange={(e) => setField("customer_phone", e.target.value)} className={inputCls} placeholder="Phone" data-testid="ls-customer-phone" /><input value={p.customer_email || ""} onChange={(e) => setField("customer_email", e.target.value)} className={inputCls} placeholder="Email" data-testid="ls-customer-email" /></div>
          <input value={p.customer_address || ""} onChange={(e) => setField("customer_address", e.target.value)} className={inputCls} placeholder="Service address" data-testid="ls-customer-address" />
        </div>
        <div className="space-y-2">
          <label className="text-xs font-semibold text-slate-500">Financing (estimate)</label>
          <div className="grid grid-cols-3 gap-2">
            <div><span className="text-[11px] text-slate-400">APR %</span><input type="number" step="0.01" value={fin.apr} onChange={(e) => setField("financing", { ...fin, apr: Number(e.target.value) })} className={inputCls} /></div>
            <div><span className="text-[11px] text-slate-400">Term (mo)</span><input type="number" value={fin.term_months} onChange={(e) => setField("financing", { ...fin, term_months: Number(e.target.value) })} className={inputCls} /></div>
            <div><span className="text-[11px] text-slate-400">Deposit %</span><input type="number" value={fin.deposit_pct} onChange={(e) => setField("financing", { ...fin, deposit_pct: Number(e.target.value) })} className={inputCls} /></div>
          </div>
          <label className="text-xs font-semibold text-slate-500">Mark as "Most Popular"</label>
          <select value={p.popular_tier || "better"} onChange={(e) => setField("popular_tier", e.target.value)} className={inputCls} data-testid="ls-popular">{TIERS.map((t) => <option key={t} value={t}>{t.toUpperCase()}</option>)}</select>
        </div>
      </div>

      {/* Equipment catalog filters — Load Calc is optional; techs choose these directly */}
      <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="ls-eq-filters">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h4 className="font-bold text-[#12355B] text-sm">Size &amp; System Type</h4>
          {(eqFilter.tonnage || eqFilter.config || eqFilter.mode) && (
            <button onClick={() => setEqFilter({ tonnage: "", config: "", mode: "" })} data-testid="ls-eq-filter-clear" className="text-xs font-semibold text-slate-500 hover:text-[#F97316]">Clear filters</button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-[11px] font-semibold text-slate-500">Tonnage</label>
            <select value={eqFilter.tonnage} onChange={(e) => setEqFilter((f) => ({ ...f, tonnage: e.target.value }))} className={inputCls} data-testid="ls-filter-tonnage">
              <option value="">Any tonnage</option>
              {tonnageOpts.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500">System Type</label>
            <select value={eqFilter.config} onChange={(e) => setEqFilter((f) => ({ ...f, config: e.target.value }))} className={inputCls} data-testid="ls-filter-config">
              <option value="">Any type</option>
              {["Split System", "Package Unit", "Mini Split"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500">System</label>
            <select value={eqFilter.mode} onChange={(e) => setEqFilter((f) => ({ ...f, mode: e.target.value }))} className={inputCls} data-testid="ls-filter-mode">
              <option value="">Heat Pump or Straight AC</option>
              {["Heat Pump", "Straight AC"].map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        </div>
        <p className="text-[11px] text-slate-400 mt-2">These control the equipment options in each tier below. Load Calculation is optional — if used, it only recommends a tonnage; you can always change it here.</p>
      </div>

      {/* Tier builders */}
      {allDiscounts.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="ls-discounts">
          <p className="text-xs font-bold text-[#12355B] mb-2">Discounts &amp; Rebates <span className="font-normal text-slate-400">(applied to all tiers; customer sees the savings, rep sees the %)</span></p>
          <div className="flex flex-wrap gap-2">
            {allDiscounts.map((d) => {
              const on = (p.discounts || []).some((c) => c.id === d.id);
              return (
                <button key={d.id} type="button" onClick={() => toggleProposalDiscount(d)} data-testid={`ls-disc-${d.id}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${on ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
                  {d.name} · {d.kind === "percent" ? `${d.amount}%` : money(d.amount)}{d.authorization_required ? " *" : ""}
                </button>
              );
            })}
          </div>
        </div>
      )}
      <div className="grid lg:grid-cols-3 gap-4">
        {TIERS.map((key) => {
          const t = p.tiers[key];
          const total = tierTotal(t);
          const dd = applyDisc(total);
          const tfin = { ...fin, ...(t.financing || {}) };
          const monthly = monthlyPayment(dd.final, tfin.apr, tfin.term_months);
          const eqSel = equipment.find((e) => e.id === t.equipment_id);
          return (
            <div key={key} className={`bg-white border rounded-xl p-4 ${p.popular_tier === key ? "border-[#F97316] ring-2 ring-[#F97316]/20" : "border-slate-200"}`} data-testid={`ls-tier-${key}`}>
              <div className="flex items-center justify-between"><h4 className="font-bold text-[#12355B] text-sm">{LABEL[key]}</h4>{p.popular_tier === key && <span className="text-[10px] font-bold bg-[#F97316] text-white px-2 py-0.5 rounded-full">MOST POPULAR</span>}</div>
              <select value={t.equipment_id} onChange={(e) => setTier(key, { equipment_id: e.target.value, lifetime: false, maintenance_plan: "" })} className={`${inputCls} mt-3`} data-testid={`ls-eq-${key}`}>
                <option value="">— Select system —</option>
                {["ACiQ", "Rheem"].map((man) => {
                  const items = equipment.filter((e) => e.source === "catalog_matchup" && (e.manufacturer || e.brand) === man && passFilter(e));
                  if (!items.length) return null;
                  return (
                    <optgroup key={man} label={`${man} — Matched Systems (${items.length})`}>
                      {items.map((e) => <option key={e.id} value={e.id}>{e.model} · {e.tonnage || e.system_type} · {money(e.price)}</option>)}
                    </optgroup>
                  );
                })}
                {(() => {
                  const anyFilter = eqFilter.tonnage || eqFilter.config || eqFilter.mode;
                  const legacy = anyFilter ? [] : equipment.filter((e) => e.source !== "catalog_matchup");
                  if (!legacy.length) return null;
                  return (
                    <optgroup label="Other / Legacy">
                      {legacy.map((e) => <option key={e.id} value={e.id}>{e.brand} {e.model} · {money(e.price)}</option>)}
                    </optgroup>
                  );
                })()}
              </select>
              {eqSel && <p className="text-xs text-slate-500 mt-1">{eqSel.system_type} · {eqSel.tonnage} · {eqSel.seer2} · {eqSel.parts_warranty}</p>}
              {t.notes && <div className="mt-2 rounded-md bg-amber-50 border border-amber-200 p-2 text-[11px] text-amber-800" data-testid={`ls-note-${key}`}><b>From matchup:</b> {t.notes}</div>}
              <div className="mt-3">
                <p className="text-xs font-semibold text-slate-500 mb-1">Comfort Upgrades</p>
                <div className="space-y-1 max-h-32 overflow-auto pr-1">
                  {addons.map((a) => (
                    <label key={a.id} className="flex items-center justify-between text-xs text-slate-700">
                      <span className="flex items-center gap-1.5"><input type="checkbox" checked={(t.addon_ids || []).includes(a.id)} onChange={() => toggleAddon(key, a.id)} /> {a.name}</span>
                      <span className="text-slate-400">{money(a.price)}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="mt-3">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-semibold text-slate-500">Install Kit <span className="font-normal text-slate-400">(manual)</span></p>
                  <button type="button" onClick={() => selectAllKit(key)} className="text-[10px] font-semibold text-[#F97316]">Select All</button>
                </div>
                <div className="space-y-1 max-h-32 overflow-auto pr-1">
                  {installKit.map((k) => (
                    <label key={k.id} className="flex items-center justify-between text-xs text-slate-700" data-testid={`ls-kit-${key}-${k.id}`}>
                      <span className="flex items-center gap-1.5"><input type="checkbox" checked={(t.install_kit_ids || []).includes(k.id)} onChange={() => toggleKit(key, k)} /> {k.name}{k.group === "line_set" ? <span className="text-[9px] text-slate-400">(one)</span> : null}</span>
                      <span className="text-slate-400">{money(k.price)}</span>
                    </label>
                  ))}
                </div>
              </div>
              <label className="mt-3 flex items-center gap-2 text-xs font-semibold text-[#12355B]" data-testid={`ls-recommend-${key}`}>
                <input type="radio" name="recommended" checked={p.recommended_tier === key} onChange={() => setRecommended(key)} /> <Star className="w-3.5 h-3.5 text-[#F97316]" /> Recommend this option
              </label>
              <div className="mt-2">
                <label className="text-[11px] font-semibold text-slate-500">Price override (optional)</label>
                <input type="number" value={t.price_override ?? ""} onChange={(e) => setTier(key, { price_override: e.target.value })} className={inputCls} placeholder="Leave blank to auto-calc" data-testid={`ls-override-${key}`} />
              </div>
              <details className="mt-2">
                <summary className="text-[11px] font-semibold text-slate-500 cursor-pointer" data-testid={`ls-fin-${key}`}>Financing (override)</summary>
                <div className="grid grid-cols-3 gap-1.5 mt-1.5">
                  <input type="number" value={t.financing?.apr ?? ""} onChange={(e) => setTierFin(key, { apr: e.target.value === "" ? undefined : Number(e.target.value) })} className={inputCls} placeholder={`APR ${fin.apr}`} data-testid={`ls-fin-apr-${key}`} />
                  <input type="number" value={t.financing?.term_months ?? ""} onChange={(e) => setTierFin(key, { term_months: e.target.value === "" ? undefined : Number(e.target.value) })} className={inputCls} placeholder={`Mo ${fin.term_months}`} />
                  <input type="number" value={t.financing?.deposit_pct ?? ""} onChange={(e) => setTierFin(key, { deposit_pct: e.target.value === "" ? undefined : Number(e.target.value) })} className={inputCls} placeholder={`Dep% ${fin.deposit_pct}`} />
                </div>
              </details>
              {eqSel?.lifetime_eligible && (
                <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-2.5">
                  <label className="flex items-center gap-2 text-xs font-semibold text-[#12355B]"><input type="checkbox" checked={!!t.lifetime} onChange={(e) => toggleLifetime(key, e.target.checked)} data-testid={`ls-lifetime-${key}`} /> <Sparkles className="w-3.5 h-3.5 text-[#F97316]" /> Include Lifetime Warranty</label>
                  {t.lifetime && (
                    <p className="mt-1.5 text-[11px] text-slate-500 flex items-start gap-1"><Lock className="w-3 h-3 mt-0.5 shrink-0" /> Required plan locked in: <b className="text-slate-700">{REQUIRED_MAINTENANCE_PLAN}</b> — 2 visits/yr. Uncheck Lifetime to remove.</p>
                  )}
                </div>
              )}
              <div className="mt-3 pt-3 border-t border-slate-100 text-center">
                {showMonthly ? <p className="text-2xl font-extrabold text-[#12355B]">${Math.round(monthly).toLocaleString()}<span className="text-sm font-medium text-slate-400">/mo*</span></p>
                  : <>
                      {dd.total > 0 && <p className="text-sm text-slate-400 line-through" data-testid={`ls-orig-${key}`}>{money(total)}</p>}
                      <p className="text-2xl font-extrabold text-[#12355B]" data-testid={`ls-total-${key}`}>{money(dd.final)}</p>
                    </>}
                <p className="text-[10px] text-slate-400">{showMonthly ? "Estimated payment" : "Total investment"}</p>
                {dd.lines.length > 0 && (
                  <div className="mt-2 text-left inline-block" data-testid={`ls-savings-${key}`}>
                    {dd.lines.map((l, i) => <p key={i} className="text-[11px] text-emerald-700 flex justify-between gap-4"><span>{l.name}</span>{" "}<span>−{money(l.amount)}</span></p>)}
                  </div>
                )}
                {dd.total > 0 && <p className="mt-1 inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5" data-testid={`ls-repdisc-${key}`} title="Visible to sales rep only — not shown to the customer">−{dd.pct.toFixed(1)}% · rep only</p>}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-slate-400">*Estimated monthly payment based on {fin.apr}% APR over {fin.term_months} months. Not a financing offer. Actual terms subject to credit approval.</p>
      {(aiLoading || insights) && (
        <div className="bg-[#12355B] text-white rounded-xl p-5" data-testid="ls-insights">
          <p className="font-bold flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#F97316]" /> AI Sales Insights <span className="text-[10px] font-normal text-slate-300">(internal only · GPT-5.6)</span></p>
          {aiLoading ? <p className="mt-2 text-sm text-slate-300 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Analyzing customer history…</p>
            : <div className="mt-2 text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">{insights}</div>}
        </div>
      )}
    </div>
  );
};
export default LaunchSelect;
